/**
 * Sessions Report API
 * API client for charging sessions using BackOffice API
 */

import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import { useAuthStore } from '@/core/stores/auth-store';
import {
  ChargingSessionsRequest,
  ChargingSessionsResponse,
  SessionBreakdownResponse,
  TariffLevelsMappingResponse,
} from '../types/api-types';
import {
  MeterValueDetailApiModel,
  MeterValuesRequest,
} from '../types/meter-value-types';

export interface OdataResponse<T> {
  value: T[];
  count?: number;
  '@odata.count'?: number;
  '@odata.nextLink'?: string;
}

export class SessionsReportApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * Get operator ID from store
   * Throws if no operator selected
   */
  private getOperatorId(): number {
    const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;
    if (!operatorId) {
      console.error('SessionsReportApi: No operator selected');
      throw new Error('No operator selected. Please select an operator first.');
    }
    return operatorId;
  }

  /**
   * Get account ID from auth store
   * Returns null if not available
   */
  private getAccountId(): string | null {
    return useAuthStore.getState().accountId;
  }

  /**
   * Build headers with operator and account IDs
   */
  private buildHeaders(baseHeaders: Record<string, string>): Record<string, string> {
    const operatorId = this.getOperatorId();
    const accountId = this.getAccountId();

    // Spread baseHeaders first, then override with our values
    const headers: Record<string, string> = {
      ...baseHeaders,
      'Content-Type': 'application/json',
      'X-Operator-Id': operatorId.toString(),
    };

    if (accountId) {
      headers['X-Account-Id'] = accountId;
    } else {
      // accountId not in auth store, check if baseHeaders has a valid value from JWT
      const jwtAccountId = baseHeaders['X-Account-Id'];
      if (!jwtAccountId || jwtAccountId === 'unknown' || jwtAccountId === 'undefined') {
        // Remove invalid X-Account-Id header - API works without it
        delete headers['X-Account-Id'];
      }
    }

    return headers;
  }

  /**
   * Build query string from request params
   * @param params - Request parameters
   * @param countOnly - If true, builds query for count-only request (count=true, no info flags)
   */
  private buildQueryString(params: ChargingSessionsRequest, countOnly: boolean = false): string {
    const queryParams: string[] = [];

    if (countOnly) {
      // Count-only query: minimal params, just need total count
      queryParams.push('count=true');
    } else {
      // Data query: full data with all info flags
      queryParams.push('count=false');
      queryParams.push('accountInfo=true');
      queryParams.push('machineInfo=true');
      queryParams.push('costInfo=true');
    }

    // Custom params (non-OData)
    if (params.actorIds) {
      queryParams.push(`actorIds=${encodeURIComponent(params.actorIds)}`);
    }
    if (params.usageTypes) {
      queryParams.push(`usageTypes=${encodeURIComponent(params.usageTypes)}`);
    }
    if (params.startSources) {
      queryParams.push(`startSources=${encodeURIComponent(params.startSources)}`);
    }
    // HIVE-6618: Device types filter
    if (params.deviceTypes) {
      queryParams.push(`deviceTypes=${encodeURIComponent(params.deviceTypes)}`);
    }

    // OData params
    if (countOnly) {
      // For count query, just need filter and minimal top
      queryParams.push('$top=1');
      queryParams.push('$skip=0');
    } else {
      // For data query, include pagination
      queryParams.push(`$skip=${params.$skip ?? 0}`);
      queryParams.push(`$top=${params.$top ?? 50}`);
    }

    if (params.$filter) {
      queryParams.push(`$filter=${encodeURIComponent(params.$filter)}`);
    }
    if (params.$orderby && !countOnly) {
      queryParams.push(`$orderby=${encodeURIComponent(params.$orderby)}`);
    }

    return queryParams.join('&');
  }

  /**
   * Get charging sessions list (data only, no count)
   * Endpoint: GET /api/v1/actor/charging?count=false
   */
  async getChargingSessions(
    params: ChargingSessionsRequest = {}
  ): Promise<ChargingSessionsResponse> {
    const authHeaders = await this.tokenManager.getAuthHeaders();
    const headers = this.buildHeaders(authHeaders);

    const queryString = this.buildQueryString({
      ...params,
      $orderby: params.$orderby || 'deviceStartDate desc',
    }, false); // count=false for data query

    const url = `${this.baseUrl}/actor/charging?${queryString}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Sessions Report API',
      '/actor/charging'
    );

    const data = await response.json();

    // Handle both OData wrapper and direct array response
    if (Array.isArray(data)) {
      return {
        value: data,
      };
    }

    return {
      value: data.value || data.rows || [],
      '@odata.nextLink': data['@odata.nextLink'],
    };
  }

  /**
   * Get total count of charging sessions matching the filter
   * Endpoint: GET /api/v1/actor/charging?count=true
   * Returns just the count, minimal data transfer
   */
  async getChargingSessionsCount(
    params: ChargingSessionsRequest = {}
  ): Promise<number> {
    const authHeaders = await this.tokenManager.getAuthHeaders();
    const headers = this.buildHeaders(authHeaders);

    const queryString = this.buildQueryString(params, true); // count=true for count query

    const url = `${this.baseUrl}/actor/charging?${queryString}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Sessions Report API',
      '/actor/charging (count)'
    );

    const data = await response.json();

    // The count should be in @odata.count or count property
    const count = data['@odata.count'] ?? data.count ?? 0;

    return count;
  }

  /**
   * Get session breakdown for tariff details
   * Endpoint: GET /api/v1/actor/sessionbreakdown/{machineId}/{sessionId}
   */
  async getSessionBreakdown(
    machineId: number,
    sessionId: number
  ): Promise<SessionBreakdownResponse> {
    const authHeaders = await this.tokenManager.getAuthHeaders();
    const headers = this.buildHeaders(authHeaders);

    const url = `${this.baseUrl}/actor/sessionbreakdown/${machineId}/${sessionId}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Sessions Report API',
      `/actor/sessionbreakdown/${machineId}/${sessionId}`
    );

    return response.json();
  }

  /**
   * Get tariff levels mapping (tariff period names for current operator)
   * Endpoint: GET /api/v1/charging/taarifLevels
   * Returns the tariff names for dynamic column headers (e.g., "שפל", "Const", "פסגה")
   */
  async getTariffLevels(): Promise<TariffLevelsMappingResponse> {
    const authHeaders = await this.tokenManager.getAuthHeaders();
    const headers = this.buildHeaders(authHeaders);

    const url = `${this.baseUrl}/charging/taarifLevels`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Sessions Report API',
      '/charging/taarifLevels'
    );

    return response.json();
  }

  /**
   * Build query string for meter values request
   */
  private buildMeterValuesQueryString(params: MeterValuesRequest): string {
    const queryParams: string[] = [];

    // Pagination
    if (params.$skip !== undefined) {
      queryParams.push(`$skip=${params.$skip}`);
    }
    if (params.$top !== undefined) {
      queryParams.push(`$top=${params.$top}`);
    }

    // Sorting
    if (params.$orderby) {
      queryParams.push(`$orderby=${encodeURIComponent(params.$orderby)}`);
    }

    // Filtering
    if (params.$filter) {
      queryParams.push(`$filter=${encodeURIComponent(params.$filter)}`);
    }

    // Custom params
    if (params.filterEmptyDelta !== undefined) {
      queryParams.push(`filterEmptyDelta=${params.filterEmptyDelta}`);
    }
    if (params.timeZoneOffset !== undefined) {
      queryParams.push(`timeZoneOffset=${params.timeZoneOffset}`);
    }
    if (params.count !== undefined) {
      queryParams.push(`count=${params.count}`);
    }

    // Cost/VAT visibility params (matching Flutter downloadFile behavior)
    if (params.addVat !== undefined) {
      queryParams.push(`addVat=${params.addVat}`);
    }
    if (params.addCost !== undefined) {
      queryParams.push(`addCost=${params.addCost}`);
    }
    if (params.addBuyCost !== undefined) {
      queryParams.push(`addBuyCost=${params.addBuyCost}`);
    }
    if (params.addSellCost !== undefined) {
      queryParams.push(`addSellCost=${params.addSellCost}`);
    }

    return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  }

  /**
   * Get meter values details for a session
   * Endpoint: GET /api/v1/actor/metervaluesdetailsbysession/{sessionId}
   */
  async getMeterValuesDetails(
    sessionId: number,
    params: MeterValuesRequest = {}
  ): Promise<OdataResponse<MeterValueDetailApiModel>> {
    const authHeaders = await this.tokenManager.getAuthHeaders();
    const headers = this.buildHeaders(authHeaders);

    // Explicitly set count=false to get actual data, not just count
    const queryString = this.buildMeterValuesQueryString({
      ...params,
      count: false,
    });
    const url = `${this.baseUrl}/actor/metervaluesdetailsbysession/${sessionId}${queryString}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Sessions Report API',
      `/actor/metervaluesdetailsbysession/${sessionId}`
    );

    const data = await response.json();

    // Handle different response formats:
    // 1. Direct array
    // 2. OData wrapper with 'value' property
    // 3. Custom wrapper with 'rows' property (this API uses 'rows')
    if (Array.isArray(data)) {
      return {
        value: data,
      };
    }

    return {
      value: data.value || data.rows || [],
      count: data['@odata.count'] ?? data.count,
      '@odata.count': data['@odata.count'],
      '@odata.nextLink': data['@odata.nextLink'],
    };
  }

  /**
   * Get meter values count for a session
   * Endpoint: GET /api/v1/actor/metervaluesdetailsbysession/{sessionId}?count=true
   */
  async getMeterValuesCount(
    sessionId: number,
    params: MeterValuesRequest = {}
  ): Promise<number> {
    const authHeaders = await this.tokenManager.getAuthHeaders();
    const headers = this.buildHeaders(authHeaders);

    const queryString = this.buildMeterValuesQueryString({
      ...params,
      count: true,
      $top: 1,
      $skip: 0,
    });
    const url = `${this.baseUrl}/actor/metervaluesdetailsbysession/${sessionId}${queryString}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Sessions Report API',
      `/actor/metervaluesdetailsbysession/${sessionId} (count)`
    );

    const data = await response.json();
    return data['@odata.count'] ?? data.count ?? 0;
  }

  /**
   * Export meter values to Excel
   * Endpoint: GET /api/v1/actor/metervaluesdetailsbysession/{sessionId}/excel
   */
  async getMeterValuesExcel(
    sessionId: number,
    params: MeterValuesRequest = {}
  ): Promise<Blob> {
    const authHeaders = await this.tokenManager.getAuthHeaders();
    const headers = this.buildHeaders(authHeaders);
    // Remove Content-Type for blob response
    delete headers['Content-Type'];

    const queryString = this.buildMeterValuesQueryString(params);
    const url = `${this.baseUrl}/actor/metervaluesdetailsbysession/${sessionId}/excel${queryString}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Sessions Report API',
      `/actor/metervaluesdetailsbysession/${sessionId}/excel`
    );

    return response.blob();
  }
}
